
import { defineComponent, PropType, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import jsVectorMap from "jsvectormap";
// The main module also sets window.jsVectorMap, so this side-effect import (which
// calls the global jsVectorMap.addMap) registers the "world" map correctly.
import "jsvectormap/dist/maps/world.js";
import "jsvectormap/dist/jsvectormap.min.css";
import { DashboardBucket } from "./trf";

// HackingClub palette: no data / lowest = gray, most threats = HC purple.
const NO_DATA = "#2b2c40"; // countries with no cases
const GRAY = "#4b4d63"; // lowest bucket (has data, few cases)
const PURPLE = "#7239ea"; // highest bucket (most cases)
const PURPLE_HOVER = "#9203dd";
const LEVELS = 6; // number of gray→purple buckets

/** Linear interpolation between two #rrggbb colors, t in [0,1]. */
function lerpColor(a: string, b: string, t: number): string {
  const ah = parseInt(a.slice(1), 16);
  const bh = parseInt(b.slice(1), 16);
  const ar = ah >> 16, ag = (ah >> 8) & 255, ab = ah & 255;
  const br = bh >> 16, bg = (bh >> 8) & 255, bb = bh & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1);
}

export default defineComponent({
  name: "CountryMap",
  props: {
    countries: { type: Array as PropType<DashboardBucket[]>, default: () => [] },
  },
  emits: ["filter"],
  setup(props, { emit }) {
    // Unique id so jsvectormap's string selector never collides.
    const mapId = "trf-worldmap-" + Math.random().toString(36).slice(2);
    const failed = ref(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let map: any = null;

    // { US: 420, BR: 88, ... } keyed by uppercase ISO-2 code.
    const values = computed<Record<string, number>>(() =>
      Object.fromEntries(props.countries.map((b) => [String(b.key).toUpperCase(), b.count]))
    );

    // --- Fallback bar chart ---
    const top = computed(() => [...props.countries].sort((a, b) => b.count - a.count).slice(0, 12));
    const series = computed(() => [{ name: "Casos", data: top.value.map((b) => b.count) }]);
    const options = computed(() => ({
      chart: {
        toolbar: { show: false },
        events: {
          dataPointSelection: (_e: unknown, _c: unknown, cfg: { dataPointIndex: number }) => {
            const code = top.value[cfg.dataPointIndex]?.key;
            if (code) emit("filter", code);
          },
        },
      },
      plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "65%" } },
      dataLabels: { enabled: false },
      colors: [PURPLE],
      xaxis: {
        categories: top.value.map((b) => b.key),
        labels: { style: { colors: "#a1a5b7" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#a1a5b7" } } },
      grid: { borderColor: "rgba(255,255,255,0.06)", strokeDashArray: 4 },
      tooltip: { theme: "dark" },
      states: { hover: { filter: { type: "lighten", value: 0.15 } } },
    }));

    const destroyMap = () => {
      try {
        map?.destroy();
      } catch (e) {
        /* ignore */
      }
      map = null;
      const el = document.getElementById(mapId);
      if (el) el.innerHTML = "";
    };

    const buildMap = async () => {
      await nextTick();
      const el = document.getElementById(mapId);
      if (!el) return;
      destroyMap();
      try {
        const vals = values.value;
        const hasData = Object.keys(vals).length > 0;

        // Bucket each country into a gray→purple level and drive the fill with an
        // OrdinalScale (scale = { levelKey: color }). jsvectormap's LinearScale
        // interpolation direction proved unreliable here, so we control the
        // mapping ourselves: higher count => higher level => more purple. sqrt
        // spreads the skewed distribution so mid countries still read purple-ish.
        const scale: Record<string, string> = {};
        for (let i = 0; i < LEVELS; i++) {
          scale["l" + i] = lerpColor(GRAY, PURPLE, i / (LEVELS - 1));
        }
        const maxCount = Math.max(1, ...Object.values(vals));
        const regionValues: Record<string, string> = {};
        for (const [code, n] of Object.entries(vals)) {
          const t = Math.sqrt(n / maxCount); // 0..1
          const level = Math.min(LEVELS - 1, Math.round(t * (LEVELS - 1)));
          regionValues[code] = "l" + level;
        }

        map = new jsVectorMap({
          selector: "#" + mapId,
          map: "world",
          zoomButtons: false,
          backgroundColor: "transparent",
          regionStyle: {
            initial: { fill: NO_DATA, stroke: "#1e1e2d", strokeWidth: 0.4 },
            hover: { fill: PURPLE_HOVER, fillOpacity: 1 },
          },
          series: hasData
            ? { regions: [{ attribute: "fill", scale, values: regionValues }] }
            : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onRegionTooltipShow(_event: unknown, tooltip: any, code: string) {
            try {
              const n = vals[code] ?? 0;
              tooltip.text(`${tooltip.text()}: ${n} caso${n === 1 ? "" : "s"}`, true);
            } catch (e) {
              /* keep default tooltip */
            }
          },
          onRegionClick(_event: unknown, code: string) {
            emit("filter", code);
          },
        });
        failed.value = false;
      } catch (e) {
        // Anything wrong at init → fall back to the bar chart, never a blank box.
        // eslint-disable-next-line no-console
        console.warn("[QuimeraX] world map init failed, using bars:", e);
        failed.value = true;
      }
    };

    onMounted(buildMap);
    onBeforeUnmount(destroyMap);

    // Rebuild when the window (data) changes, unless we already fell back.
    watch(
      () => props.countries,
      () => {
        if (!failed.value) buildMap();
      },
      { deep: true }
    );

    return { mapId, failed, series, options };
  },
});
